// What happened to a bill after it was issued, for a printed copy (docs/plans/reprint-credits.md).
// The invoice's own lines and totals never change; this is a separate block under them.
import type { RowOf } from '@ims/data'

type CreditNoteRow = RowOf<'credit_notes'>

export interface BillAdjustments {
  /** Finalized credit notes against the bill, oldest first. */
  credits: { number: string | null; note_date: string; total_paisa: number }[]
  /** Active payments allocated to the bill. */
  paidPaisa: number
  /** Total − paid − credited: below zero is a refund owed to the customer. */
  balancePaisa: number
}

/**
 * The block for a finalized bill with at least one finalized credit note, else null (a
 * bill nobody returned anything from prints as it always did). The amount paid comes
 * from the balance, which is total − active payments − finalized credit notes.
 */
export function billAdjustments(
  invoice: { status: string; total_paisa: number },
  balancePaisa: number | null,
  creditNotes: readonly Pick<CreditNoteRow, 'status' | 'number' | 'note_date' | 'total_paisa'>[],
): BillAdjustments | null {
  if (invoice.status !== 'finalized' || balancePaisa === null) return null
  const credits = creditNotes
    .filter((c) => c.status === 'finalized')
    .map((c) => ({ number: c.number, note_date: c.note_date, total_paisa: c.total_paisa }))
    .sort((a, b) => a.note_date.localeCompare(b.note_date))
  if (credits.length === 0) return null
  const credited = credits.reduce((sum, c) => sum + c.total_paisa, 0)
  return {
    credits,
    paidPaisa: invoice.total_paisa - credited - balancePaisa,
    balancePaisa,
  }
}

/**
 * The words for the last line: what is still owed, nothing, or what is owed back. The public
 * invoice page has its own copy (`balanceLine` in apps/api/src/routes/portal.tsx).
 */
export function balanceLine(balancePaisa: number): 'Balance due' | 'Settled' | 'Refund due' {
  return balancePaisa > 0 ? 'Balance due' : balancePaisa < 0 ? 'Refund due' : 'Settled'
}
